'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import type { ArticleAuthor, ArticleCategory } from '@jlg-website/database/types';
import type { ArticleInput } from '@jlg-website/shared/schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const loading = () => <>Loading...</>;

const ArticlePostForm = dynamic(
  () => import('@/components/admin/article/articles/article-form').then((mod) => mod.ArticleForm),
  { loading }
);

type FormData = {
  authors: ArticleAuthor[];
  categories: ArticleCategory[];
};

export function ArticlePostEditDialogForm({
  value,
  onEdit,
}: {
  value: string;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [defaultValues, setDefaultValues] = useState<ArticleInput | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;
    setFormData(null);
    setDefaultValues(null);
    Promise.all([
      fetch('/admin/api/article/articles/form-data', { cache: 'no-store' }).then((r) => r.json()),
      fetch(`/admin/api/article/articles/${value}`, { cache: 'no-store' }).then((r) => r.json()),
    ]).then(([fd, post]) => {
      setFormData(fd);
      setDefaultValues(post);
    });
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setFormData(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" aria-label="Edit">
          <Pencil /> <strong>Edit Article</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Article</DialogTitle>
        </DialogHeader>
        {!formData || !defaultValues ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ArticlePostForm
            authors={formData.authors}
            categories={formData.categories}
            defaultValues={defaultValues}
            onSave={handleSave}
            customCancelButton={
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ArticlePostEditDialogForm;
